import React from "react";
import { Flex, Text, type AgGrid } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { SupportedColumns } from "../columnDefinitions";
import { type CashAccountRowItem } from "../types";
import { AccountLabel } from "./AccountLabel";

/**
 * Counts the leaf (bank-account) rows under a group node, recursing through any
 * intermediate group levels (e.g. Collection → Entity → accounts).
 */
const countLeafAccounts = (node: AgGrid.IRowNode<CashAccountRowItem>): number => {
  if (!node.group) return 1;
  return (node.childrenAfterGroup ?? []).reduce(
    (sum, child) => sum + countLeafAccounts(child),
    0,
  );
};

/**
 * The human label for a group level, derived from its grouping column. In
 * Entity mode a top-level node may be a Collection or (for collection-less
 * entities, via `groupAllowUnbalanced`) an Entity — this is what distinguishes
 * "Collection · N bank accounts" from "Entity · N bank accounts" in the design.
 */
const groupTypeLabel = (field?: string | null): string => {
  switch (field) {
    case SupportedColumns.Collection:
      return "Collection";
    case SupportedColumns.Bank:
      return "Bank";
    case SupportedColumns.Entity:
    default:
      return "Entity";
  }
};

/**
 * Custom `innerRenderer` for the auto group column. Replaces AG Grid's native
 * "(N)" count with the two-line design: the group name (Collection / Entity /
 * Bank) above a muted "{type} · {N} bank accounts" subtitle. The native count
 * is suppressed via `suppressCount: true` on `autoGroupColumnDef`.
 */
export const GroupCell: React.FC<AgGrid.ICellRendererParams<CashAccountRowItem>> = (params) => {
  const label = typeof params.value === "string" ? params.value : "";

  // Leaf (bank-account) rows: the account label "{bankName} ....{last4}" (from the
  // tree column's `field`) over a muted "As of: {timestamp}" subtitle.
  if (!params.node?.group) {
    return <AccountLabel label={label} asOf={params.data?.asOf ?? ""} />;
  }

  const count = params.node ? countLeafAccounts(params.node) : 0;
  const noun = count === 1 ? "bank account" : "bank accounts";
  const typeLabel = groupTypeLabel(params.node?.field);

  return (
    <Flex flexDirection="column" gap={Sizes.XS}>
      <Text variant="regular-md">{label}</Text>
      <Text variant="secondary-sm">
        {typeLabel} · {count} {noun}
      </Text>
    </Flex>
  );
};

export default GroupCell;
